<template>
  <ion-page v-if="documentSelected">
    <ion-content>
      <LargeNavbar
        @backButton="backButton"
        show-back-button
        :title="t('profile.myDocumentDetailTitle', { type: documentSelected?.name ?? '' })"
        :show-actions="false"
      />
      <div class="d-flex w-100 justify-space-center">
        <div class="content w-100 mt-8 ion-padding document-detail-wrapper">
          <ion-row class="">
            <ion-col
              class="document-detail-wrapper__image"
              size="12"
              v-if="
                documentSelected &&
                documentSelected.Inputs &&
                documentSelected.Inputs[0].type !== DOCUMENT_TYPES.CONTRACT
              "
            >
              <div v-if="fileInputAnswers.length > 0">
                <div
                  v-for="(answer, index) in fileInputAnswers"
                  :key="index"
                  class="image-container"
                >
                  <img :src="answer.value" />
                  <ion-icon :icon="pencil" class="edit-icon" @click="openEdit"></ion-icon>
                </div>
              </div>
              <div v-else>
                <ion-button
                  @click="openEdit"
                  fill="outline"
                  expand="block"
                  size="default"
                  class="padding-y-small mb-xs text-weight-bold"
                >
                  <ion-icon :icon="add" class="ion-margin-end"></ion-icon>
                  {{ t('files.Add document image') }}
                </ion-button>
              </div>
            </ion-col>

            <ion-col v-else class="document-detail-wrapper__image" size="12">
              <div class="document-detail-wrapper__preview">
                <ion-icon :icon="documentOutline" size="large" color="primary"></ion-icon>
                <ion-button
                  fill="outline"
                  size="small"
                  class="padding-x-small padding-y-small mt-8"
                  @click="openDownloadSign"
                >
                  <ion-icon :icon="eyeOutline" class="ion-margin-end"></ion-icon>
                  {{ t('actions.viewFile') }}
                </ion-button>
              </div>
            </ion-col>
          </ion-row>
          <ion-row
            class="mt-8"
            v-if="documentSelected.status == DOCUMENT_VERIFICATION_STATUSES.REJECTED"
          >
            <ion-col size="12">
              <p v-if="documentSelected.reasonForRejection" class="mt-8 mb-16">
                {{ documentSelected.reasonForRejection }}
              </p>
              <ion-button fill="solid" expand="block" @click="openManualSignatureContract"
                >{{ t('files.replaceNow') }}
              </ion-button>
            </ion-col>
          </ion-row>
          <ion-row class="mt-8">
            <ion-col size="12">
              <h4 class="h4 text-weight-bold text-color-placeholder mb-16">
                {{ t('files.status.verificationStatus') }}
              </h4>
              <TagStatusDocument
                :status="documentSelected.status ?? DOCUMENT_VERIFICATION_STATUSES.PENDING"
              />
            </ion-col>
          </ion-row>
          <ion-row class="mt-8" v-if="firstDateInputAnswer?.updatedAt">
            <ion-col size="12">
              <h4 class="h4 text-weight-bold text-color-placeholder">
                {{ t('files.expires.expiration') }}
              </h4>
              <p>{{ formatDateTime(firstDateInputAnswer?.value as string, locale, true, true) }}</p>
            </ion-col>
          </ion-row>
          <ion-row
            class="mt-8"
            v-if="firstDateInputAnswer?.updatedAt || documentSelected?.updatedAt"
          >
            <ion-col size="12">
              <h4 class="h4 text-weight-bold text-color-placeholder">
                {{ t('files.expires.lastUpdated') }}
              </h4>
              <p>
                {{
                  documentSelected.updatedAt
                    ? formatDateTime(documentSelected?.updatedAt as string, locale, true, true)
                    : formatDateTime(firstDateInputAnswer?.updatedAt as string, locale, true, true)
                }}
              </p>
            </ion-col>
          </ion-row>
          <ion-row class="mt-8">
            <ion-col size="12">
              <h4 class="h4 text-weight-bold text-color-placeholder mb-16">
                {{ t('files.expires.expirationStatus') }}
              </h4>
              <TagExpirationDocument :date="firstDateInputAnswer?.value ?? ''" />
            </ion-col>
          </ion-row>
        </div>
      </div>
    </ion-content>

    <ion-modal
      :is-open="toggleUploadPhoto"
      :initial-breakpoint="0.93"
      :breakpoints="[0, 0.25, 0.5, 0.7, 0.93]"
      @will-dismiss="toggleUploadPhoto = false"
    >
      <UploadDocumentModal
        v-if="documentSelected"
        :get-document-selected="documentSelected"
        @close="toggleUploadPhoto = false"
      />
    </ion-modal>
  </ion-page>
</template>
<script setup lang="ts">
  import { IonContent, IonPage, IonRow, IonCol, IonIcon, IonButton, IonModal } from '@ionic/vue';
  import { computed, ref } from 'vue';
  import { add, pencil } from 'ionicons/icons';
  import { useI18n } from 'vue-i18n';
  import { useProfile } from '@/composables/useProfile';
  import { formatDateTime } from '@/utils/date-utils';
  import {
    DOCUMENT_TYPES,
    DOCUMENT_VERIFICATION_STATUSES,
  } from '@/utils/constants/general-constants';
  import { storeToRefs } from 'pinia';
  import { documentOutline, eyeOutline } from 'ionicons/icons';
  import { useBookingStore } from '@/stores/booking-store';
  import { useBooking } from '@/composables/useBooking';
  import LargeNavbar from '@/components/LargeNavbar.vue';
  import router from '@/router';
  import TagExpirationDocument from '@/components/shared/TagExpirationDocument.vue';
  import TagStatusDocument from '@/components/shared/TagStatusDocument.vue';
  import UploadDocumentModal from '@/components/profile/documents/UploadDocumentModal.vue';

  const { documentSelected, documentForm } = useProfile();
  const { toggleUploadManualContract, lastBooking } = storeToRefs(useBookingStore());
  const { handleDownloadContract } = useBooking();
  const isUpdated = ref<boolean>(false);
  const toggleUploadPhoto = ref(false);

  const fileInputAnswers = computed(() => {
    if (
      documentSelected.value &&
      documentSelected.value.Inputs &&
      documentSelected.value.Inputs.length > 0
    ) {
      const fileInput = documentSelected.value.Inputs.find((input) => input.type === 'file');
      if (fileInput && fileInput.Answers && fileInput.Answers.length > 0) {
        return fileInput.Answers;
      }
    }
    return [];
  });

  const firstDateInputAnswer = computed(() => {
    if (documentSelected.value) {
      const fileInput = documentSelected.value!.Inputs.find((input) => input.type === 'date');
      if (fileInput && fileInput.Answers.length > 0) {
        return fileInput.Answers[0];
      }
    }
    return null;
  });

  const { t, locale } = useI18n();

  const openDownloadSign = () => {
    if (lastBooking?.value && lastBooking?.value?.id) {
      handleDownloadContract(lastBooking.value.id).then((result) => {
        if (result?.data) {
          const blob = new Blob([result?.data], { type: 'application/pdf' });
          const link = document.createElement('a');
          link.href = window.URL.createObjectURL(blob);
          link.download = `contract_${lastBooking?.value?.id}.pdf`;
          link.click();
        }
      });
    }
  };

  const backButton = () => {
    router.back();
  };
  const openManualSignatureContract = () => {
    toggleUploadManualContract.value = true;
    router.push({ name: 'profile.documents' });
  };

  const openEdit = () => {
    isUpdated.value = true;
    documentForm.value = {
      numberId: '',
      expirationDate:
        firstDateInputAnswer.value && firstDateInputAnswer.value
          ? firstDateInputAnswer.value.value
          : undefined,
      renewalDate: '',
    };
    toggleUploadPhoto.value = true;
  };
</script>
<style scoped lang="scss">
  ion-button {
    height: 36px;
  }

  ion-modal {
    --border-radius: 25px;
    --background: var(--ion-color-body);
  }

  ion-content {
    --border-radius: 25px;
    --background: white;
  }

  .document-detail-wrapper {
    &__image {
      img {
        width: 100%;
        max-height: 350px;
        object-fit: cover;
        border-radius: 20px;
      }
    }

    &__preview {
      height: 170px;
      width: 100%;
      background: white;
      display: flex;
      align-items: center;
      justify-content: center;
      border-radius: 10px;
      flex-direction: column;
      box-shadow: 1px 1px 2px 1px rgba(0, 0, 0, 0.2);

      img {
        width: 100%;
        height: 190px;
        object-fit: cover;
      }

      h1 {
        font-size: 30px;
        font-weight: bold;
        color: white;
      }
    }
  }
  .image-container {
    position: relative;
    display: inline-block;
    width: 100%;
    max-height: 350px;
  }


  .edit-icon {
    position: absolute;
    top: 10px;
    right: 10px;
    padding: 0.3rem;
    font-size: 1.8rem;
    border-radius: 50%;
    background-color: rgba(0, 0, 0, 0.5);
    color: white;
    cursor: pointer;
  }
</style>
